import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';
import { environment } from '@env/environment';
import { HandlerError, HttpErrorHandlerService } from './http-error-handler.service';
import { BaseResponse } from '../models/base-response';
import { SKIP_LOADING } from '../interceptadores/loading-interceptor.service';

export interface MensagemSecretaria {
  id: string;
  texto: string;
  autor: 'usuario' | 'secretaria';
  data: string; // ISO
  opcoes?: string[]; // opções de resposta rápida (quick replies)
}

const STORAGE_KEY = 'secretariaMessag';
export interface CliqueOpcaoSecretaria {
  id: string;
  mensagemId: string;
  texto: string;
  data: string; // ISO
  contexto?: string;
}
const STORAGE_KEY_CLIQUES = 'secretariaCliques';

@Injectable({ providedIn: 'root' })
export class SecretariaService {
  private get nowIso() { return new Date().toISOString(); }

  // HTTP config
  private handlerErro: HandlerError;
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient, httpErrorHandler: HttpErrorHandlerService) {
    this.handlerErro = httpErrorHandler.createHandleError('SecretariaService');
  }

  // Estado de abertura do chat (componente flutuante)
  private _aberto$ = new BehaviorSubject<boolean>(false);
  isAberto$ = this._aberto$.asObservable();

  private _ultimoClique$ = new BehaviorSubject<CliqueOpcaoSecretaria | null>(null);
  ultimoClique$ = this._ultimoClique$.asObservable();

  abrir() { this._aberto$.next(true); }
  fechar() { this._aberto$.next(false); }

  getMensagens(): MensagemSecretaria[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as MensagemSecretaria[];
      return [];
    } catch {
      return [];
    }
  }

  private salvar(lista: MensagemSecretaria[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
  }

  adicionarMensagemUsuario(texto: string) {
    if (!texto || !texto.trim()) return;
    const lista = this.getMensagens();
    lista.push({ id: crypto.randomUUID?.() || Math.random().toString(36).slice(2), texto: texto.trim(), autor: 'usuario', data: this.nowIso });
    this.salvar(lista);
  }

  adicionarMensagemSecretaria(texto: string) {
    if (!texto || !texto.trim()) return;
    const lista = this.getMensagens();
    lista.push({ id: crypto.randomUUID?.() || Math.random().toString(36).slice(2), texto: texto.trim(), autor: 'secretaria', data: this.nowIso });
    this.salvar(lista);
  }

  adicionarMensagemSecretariaComOpcoes(texto: string, opcoes: string[]) {
    if (!opcoes || opcoes.length === 0) return this.adicionarMensagemSecretaria(texto);
    const lista = this.getMensagens();
    lista.push({ id: crypto.randomUUID?.() || Math.random().toString(36).slice(2), texto: (texto || '').trim(), autor: 'secretaria', data: this.nowIso, opcoes: [...opcoes] });
    this.salvar(lista);
  }

  limparOpcoes(id: string) {
    const lista = this.getMensagens();
    const idx = lista.findIndex(m => m.id === id);
    if (idx >= 0) {
      const { opcoes, ...rest } = lista[idx];
      lista[idx] = { ...rest } as any;
      this.salvar(lista);
    }
  }

  getCliques(): CliqueOpcaoSecretaria[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CLIQUES);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as CliqueOpcaoSecretaria[];
      return [];
    } catch {
      return [];
    }
  }

  private salvarCliques(lista: CliqueOpcaoSecretaria[]) {
    localStorage.setItem(STORAGE_KEY_CLIQUES, JSON.stringify(lista));
  }

  registrarCliqueOpcao(mensagemId: string, texto: string, contexto?: string) {
    const t = (texto || '').trim();
    if (!t) return;
    const lista = this.getCliques();
    const item: CliqueOpcaoSecretaria = {
      id: (crypto as any).randomUUID?.() || Math.random().toString(36).slice(2),
      mensagemId,
      texto: t,
      data: this.nowIso,
      contexto: (contexto || '').trim() || undefined
    };
    lista.push(item);
    this.salvarCliques(lista);
    this._ultimoClique$.next(item);
  }

  limparCliques() { this.salvarCliques([]); }

  // =================== API BACKEND - AgenteSecretaria ===================
  buscarMensagens(): Observable<BaseResponse<any> | BaseResponse<any>> {
    return this.http
      .get<BaseResponse<any>>(
        `${this.baseUrl}/AgenteSecretaria/BuscarMensagens`,
        { context: new HttpContext().set(SKIP_LOADING, true) }
      )
      .pipe(catchError(this.handlerErro<BaseResponse<any>>('buscarMensagens')));
  }

  buscarUltimaMensagem(): Observable<BaseResponse<any> | BaseResponse<any>> {
    return this.http
      .get<BaseResponse<any>>(
        `${this.baseUrl}/AgenteSecretaria/BuscarUltimaMensagem`,
        { context: new HttpContext().set(SKIP_LOADING, true) }
      )
      .pipe(catchError(this.handlerErro<BaseResponse<any>>('buscarUltimaMensagem')));
  }

  enviarMensagem(mensagem: string): Observable<BaseResponse<boolean> | BaseResponse<any>> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.baseUrl}/AgenteSecretaria/EnviarMensagem?mensagem="${encodeURIComponent(mensagem ?? '')}"`,
        {},
        { context: new HttpContext().set(SKIP_LOADING, true) }
      )
      .pipe(catchError(this.handlerErro<BaseResponse<any>>('enviarMensagem')));
  }

  limpar() { this.salvar([]); }
}
